// Incident responsibility and the damage cost trail.
//
// One incident -> who is responsible (one or more drivers, split by fixed
// amount or percentage) -> the excess each owes -> the deduction that
// collects it and every payment made against it, plus the repair records
// (parts / labour / quotes) that explain the cost. Works for an insurance
// claim and for a repair handled with no claim at all: everything hangs off
// the incident, not the claim.
//
// The database does the real work atomically (confirm_incident_excess,
// migration 0119) and re-checks every rule below; the pure functions here
// exist so the form can show a live running total and so the rules are unit
// tested (incidentResponsibility.test.ts).
import { supabase } from "@/lib/supabase";
import { num, round2 } from "@/lib/driverDeductions";

export type SplitMode = "fixed" | "percent";

export interface SplitEntry {
  driverName: string;
  /** A dollar amount (fixed) or a percentage (percent). */
  value: number;
}

export interface SplitResult {
  /** Each driver's share in dollars, in the same order as the entries. Exactly adds up to the excess. */
  shares: number[];
  /** How much of the split has been handed out, in the mode's own units ($ or %). */
  allocated: number;
  /** What's still to hand out, in the mode's own units ($ or %). */
  unallocated: number;
  error: string | null;
}

const TOLERANCE = 0.005;

export function computeShares(total: number, mode: SplitMode, entries: SplitEntry[]): SplitResult {
  const t = round2(total);
  const target = mode === "percent" ? 100 : t;
  const allocated = round2(entries.reduce((s, e) => s + (Number.isFinite(e.value) ? e.value : 0), 0));
  const unallocated = round2(target - allocated);
  const fail = (error: string): SplitResult => ({ shares: entries.map(() => 0), allocated, unallocated, error });

  if (entries.length === 0) return fail("Choose at least one responsible driver");
  if (new Set(entries.map((e) => e.driverName)).size !== entries.length) return fail("A driver can only be listed once");
  if (entries.some((e) => !e.driverName)) return fail("Choose a driver for every line");
  if (entries.some((e) => !Number.isFinite(e.value) || e.value < 0)) return fail("Allocations can't be negative");
  if (t <= 0) return fail("Enter the excess amount");

  if (Math.abs(unallocated) > TOLERANCE) {
    return fail(
      mode === "percent"
        ? `The percentages add up to ${allocated} but must total 100`
        : `The amounts add up to $${allocated.toFixed(2)} but the excess is $${t.toFixed(2)}`
    );
  }

  const shares = entries.map((e) => (mode === "percent" ? round2((t * e.value) / 100) : round2(e.value)));
  // Cents lost to rounding go to the largest share so the shares add up exactly.
  const remainder = round2(t - shares.reduce((s, v) => s + v, 0));
  if (remainder !== 0) {
    let largest = 0;
    shares.forEach((v, i) => {
      if (v > shares[largest]) largest = i;
    });
    shares[largest] = round2(shares[largest] + remainder);
  }
  return { shares, allocated, unallocated: 0, error: null };
}

/** An even starting split for the form: the last driver takes any odd cents. */
export function evenSplit(total: number, mode: SplitMode, driverNames: string[]): SplitEntry[] {
  const n = driverNames.length;
  if (n === 0) return [];
  const target = mode === "percent" ? 100 : round2(total);
  const each = round2(target / n);
  return driverNames.map((driverName, i) => ({
    driverName,
    value: i === n - 1 ? round2(target - each * (n - 1)) : each,
  }));
}

// ---------------------------------------------------------------------------
// Which fault classifications restart a driver's insurance-excess cycle
// ---------------------------------------------------------------------------

export const FAULT_CLASSIFICATION_OPTIONS: { key: string; label: string; hint: string }[] = [
  { key: "at_fault", label: "At fault", hint: "Our driver caused it." },
  { key: "section2_with_repair_cost", label: "Section 2 + repair cost", hint: "Insurer covers it but there's a repair cost to us." },
  { key: "section2", label: "Section 2 (protection only)", hint: "No cost to us, but it stays on the driver's claim history." },
  { key: "not_at_fault", label: "Not at fault", hint: "A third party was at fault." },
  { key: "repair_internally", label: "Repaired internally", hint: "Fixed in-house, no claim." },
];

export const RESET_SETTING_NAME = "insurance_excess_reset_classifications";
export const DEFAULT_RESET_CLASSIFICATIONS = ["at_fault"];

/** An empty saved value means "none restart it" (deliberate); a missing setting means the default. */
export function parseResetClassifications(raw: string | null | undefined): string[] {
  if (raw === null || raw === undefined) return [...DEFAULT_RESET_CLASSIFICATIONS];
  const known = new Set(FAULT_CLASSIFICATION_OPTIONS.map((o) => o.key));
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => known.has(s));
}

export function restartsCycle(classification: string | null | undefined, resetList: string[]): boolean {
  return !!classification && resetList.includes(classification);
}

export async function loadResetClassifications(): Promise<string[]> {
  const { data, error } = await supabase.from("settings").select("value").eq("name", RESET_SETTING_NAME).maybeSingle();
  if (error) throw error;
  return parseResetClassifications(data ? (data.value ?? "") : null);
}

export function resetSettingRow(keys: string[]) {
  return {
    name: RESET_SETTING_NAME,
    value: keys.join(","),
    category: "InsuranceExcess",
    label: "Claims That Restart a Driver's Excess Cycle",
    description:
      "Which fault classifications restart a driver's insurance-excess tenure and repayment cycle when their responsibility for an incident is confirmed. Every claim stays in the driver's claim history either way.",
    type: "text",
    sort_order: 90,
    is_active: true,
  };
}

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

export interface ConfirmAllocation {
  driverName: string;
  type?: SplitMode;
  value?: number;
  weekly?: number;
  /** Points at a deduction that was already set up by hand, instead of creating a new one. Only valid when no new excess applies. */
  linkAdjustmentId?: string;
}

export interface LinkableCharge {
  id: string;
  driver_name: string;
  description: string | null;
  total_amount: number | string | null;
  remaining_balance: number | string | null;
  deduction_status: string | null;
}

/** Reducing-balance charges not yet attached to an incident -- e.g. "Damage to 416" set up by hand before incidents could be linked. */
export async function loadLinkableCharges(): Promise<LinkableCharge[]> {
  const [adjRes, linkedRes] = await Promise.all([
    supabase
      .from("driver_financial_adjustments")
      .select("id, driver_name, description, total_amount, remaining_balance, deduction_status")
      .eq("adjustment_type", "deduction")
      .eq("adjustment_mode", "reducing_balance")
      .neq("deduction_status", "cancelled")
      .order("driver_name"),
    supabase.from("insurance_incident_responsibilities").select("adjustment_id").is("voided_at", null).not("adjustment_id", "is", null),
  ]);
  if (adjRes.error) throw adjRes.error;
  if (linkedRes.error) throw linkedRes.error;
  const taken = new Set(((linkedRes.data as { adjustment_id: string }[] | null) ?? []).map((r) => r.adjustment_id));
  return ((adjRes.data as LinkableCharge[] | null) ?? []).filter((c) => !taken.has(c.id));
}

export interface ConfirmResult {
  restart_cycle: boolean;
  allocations: { driver_name: string; allocated: number; weekly: number | null; adjustment_id: string | null; restarted_cycle: boolean }[];
}

export async function confirmIncidentExcess(args: {
  incidentId: string;
  excessApplies: boolean;
  excessAmount: number;
  allocations: ConfirmAllocation[];
  description: string;
}): Promise<ConfirmResult> {
  const { data, error } = await supabase.rpc("confirm_incident_excess", {
    p_incident_id: args.incidentId,
    p_excess_applies: args.excessApplies,
    p_excess_amount: args.excessApplies ? args.excessAmount : 0,
    p_allocations: args.allocations.map((a) => ({
      driver_name: a.driverName,
      type: a.type,
      value: a.value,
      weekly: a.weekly,
      link_adjustment_id: a.linkAdjustmentId,
    })),
    p_description: args.description,
  });
  if (error) throw error;
  return data as ConfirmResult;
}

export async function voidIncidentResponsibilities(incidentId: string, note: string) {
  const { data, error } = await supabase.rpc("void_incident_responsibilities", { p_incident_id: incidentId, p_note: note });
  if (error) throw error;
  return data as { voided: number; charges_cancelled: number; fund_entries_kept_because_already_collected: number };
}

// ---------------------------------------------------------------------------
// The cost trail for one incident
// ---------------------------------------------------------------------------

export interface ResponsibilityRow {
  id: string;
  incident_id: string;
  driver_name: string;
  allocation_type: SplitMode | null;
  allocation_value: number | string;
  allocated_amount: number | string;
  weekly_amount: number | string | null;
  adjustment_id: string | null;
  restarted_cycle: boolean;
  fault_classification: string | null;
  voided_at: string | null;
  created_at: string;
}

export interface TrailShare extends ResponsibilityRow {
  /** The collecting deduction's current state, if one was created. */
  status: string | null;
  remaining: number | null;
  paid: number;
}

export interface RepairRecord {
  id: string;
  vehicle_number: string;
  system_name: string | null;
  work_type_name: string | null;
  service_date: string;
  cost: number | string | null;
  is_labour: boolean;
  supplier: string | null;
  notes: string | null;
  incident_id: string | null;
}

export interface CostTrail {
  shares: TrailShare[];
  voided: ResponsibilityRow[];
  repairs: RepairRecord[];
  repairsTotal: number;
  partsTotal: number;
  labourTotal: number;
  /** Repair records for the same vehicle that aren't attached to any incident yet. */
  unlinkedRepairs: RepairRecord[];
  evidence: { photos: number; documents: number };
}

const REPAIR_COLUMNS = "id, vehicle_number, system_name, work_type_name, service_date, cost, is_labour, supplier, notes, incident_id";

export async function loadCostTrail(incidentId: string, vehicleNumber: string | null): Promise<CostTrail> {
  const [respRes, repairRes, photoRes, docRes] = await Promise.all([
    supabase
      .from("insurance_incident_responsibilities")
      .select("id, incident_id, driver_name, allocation_type, allocation_value, allocated_amount, weekly_amount, adjustment_id, restarted_cycle, fault_classification, voided_at, created_at")
      .eq("incident_id", incidentId)
      .order("created_at"),
    supabase.from("maintenance_records").select(REPAIR_COLUMNS).eq("incident_id", incidentId).order("service_date"),
    supabase.from("insurance_incident_photos").select("id", { count: "exact", head: true }).eq("incident_id", incidentId),
    supabase
      .from("insurance_claims")
      .select("id")
      .eq("incident_id", incidentId)
      .then(async ({ data }) => {
        const ids = (data || []).map((c: { id: string }) => c.id);
        if (ids.length === 0) return { count: 0 };
        return supabase.from("insurance_claim_documents").select("id", { count: "exact", head: true }).in("claim_id", ids);
      }),
  ]);
  if (respRes.error) throw respRes.error;
  if (repairRes.error) throw repairRes.error;

  const all = (respRes.data as ResponsibilityRow[] | null) ?? [];
  const live = all.filter((r) => !r.voided_at);
  const voided = all.filter((r) => !!r.voided_at);

  const adjustmentIds = live.map((r) => r.adjustment_id).filter((id): id is string => !!id);
  const adjById = new Map<string, { deduction_status: string | null; remaining_balance: number | string | null }>();
  const paidByAdj = new Map<string, number>();
  if (adjustmentIds.length > 0) {
    const [{ data: adjRows }, { data: paidRows }] = await Promise.all([
      supabase.from("driver_financial_adjustments").select("id, deduction_status, remaining_balance").in("id", adjustmentIds),
      supabase
        .from("driver_deduction_transactions")
        .select("adjustment_id, amount")
        .eq("txn_type", "deduction")
        .is("reversed_at", null)
        .in("adjustment_id", adjustmentIds),
    ]);
    ((adjRows as { id: string; deduction_status: string | null; remaining_balance: number | string | null }[] | null) ?? []).forEach((a) =>
      adjById.set(a.id, a)
    );
    ((paidRows as { adjustment_id: string; amount: number | string }[] | null) ?? []).forEach((p) =>
      paidByAdj.set(p.adjustment_id, round2((paidByAdj.get(p.adjustment_id) ?? 0) + num(p.amount)))
    );
  }

  const shares: TrailShare[] = live.map((r) => {
    const adj = r.adjustment_id ? adjById.get(r.adjustment_id) : undefined;
    return {
      ...r,
      status: adj?.deduction_status ?? null,
      remaining: adj && adj.remaining_balance !== null ? num(adj.remaining_balance) : null,
      paid: r.adjustment_id ? paidByAdj.get(r.adjustment_id) ?? 0 : 0,
    };
  });

  const repairs = (repairRes.data as RepairRecord[] | null) ?? [];
  const labourTotal = round2(repairs.filter((r) => r.is_labour).reduce((s, r) => s + num(r.cost), 0));
  const partsTotal = round2(repairs.filter((r) => !r.is_labour).reduce((s, r) => s + num(r.cost), 0));

  let unlinkedRepairs: RepairRecord[] = [];
  if (vehicleNumber) {
    const { data } = await supabase
      .from("maintenance_records")
      .select(REPAIR_COLUMNS)
      .eq("vehicle_number", vehicleNumber)
      .is("incident_id", null)
      .order("service_date", { ascending: false })
      .limit(30);
    unlinkedRepairs = (data as RepairRecord[] | null) ?? [];
  }

  return {
    shares,
    voided,
    repairs,
    repairsTotal: round2(partsTotal + labourTotal),
    partsTotal,
    labourTotal,
    unlinkedRepairs,
    evidence: { photos: photoRes.count ?? 0, documents: (docRes as { count: number | null }).count ?? 0 },
  };
}

export async function linkRepairRecord(repairId: string, incidentId: string | null): Promise<void> {
  const { error } = await supabase.from("maintenance_records").update({ incident_id: incidentId }).eq("id", repairId);
  if (error) throw error;
}

export async function addRepairLine(input: {
  incidentId: string;
  vehicleNumber: string;
  date: string;
  description: string;
  cost: number;
  isLabour: boolean;
  supplier: string;
}): Promise<void> {
  const { error } = await supabase.from("maintenance_records").insert({
    vehicle_number: input.vehicleNumber,
    system_name: "Accident repair",
    work_type_name: input.description,
    service_date: input.date,
    cost: input.cost,
    is_labour: input.isLabour,
    supplier: input.supplier || null,
    incident_id: input.incidentId,
  });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// A driver's damage / claim history (every claim, protection-only included)
// ---------------------------------------------------------------------------

export interface HistoryEntry {
  key: string;
  incidentId: string;
  incidentDate: string;
  driverName: string;
  /** "driver" = the driver on the incident; "responsible" = also/only named as responsible. */
  role: "driver" | "responsible";
  vehicleNumber: string | null;
  category: string;
  faultClassification: string | null;
  claimType: string | null;
  claimStatus: string | null;
  excessApplies: boolean | null;
  allocated: number | null;
  paid: number;
  remaining: number | null;
  deductionStatus: string | null;
  restartedCycle: boolean;
}

export function buildDriverHistory(args: {
  incidents: {
    id: string;
    incident_date: string;
    driver_name: string | null;
    vehicle_number: string | null;
    incident_category: string;
    fault_classification: string | null;
    excess_applies: boolean | null;
  }[];
  claims: { incident_id: string; claim_type: string | null; status: string | null }[];
  responsibilities: ResponsibilityRow[];
  adjustments: Map<string, { deduction_status: string | null; remaining_balance: number | string | null }>;
  paidByAdjustment: Map<string, number>;
}): HistoryEntry[] {
  const claimByIncident = new Map(args.claims.map((c) => [c.incident_id, c]));
  const respByKey = new Map<string, ResponsibilityRow>();
  args.responsibilities.filter((r) => !r.voided_at).forEach((r) => respByKey.set(`${r.incident_id}|${r.driver_name}`, r));

  const incidentById = new Map(args.incidents.map((i) => [i.id, i]));
  const entries: HistoryEntry[] = [];
  const seen = new Set<string>();

  const make = (incidentId: string, driverName: string, role: HistoryEntry["role"]) => {
    const inc = incidentById.get(incidentId);
    if (!inc) return;
    const key = `${incidentId}|${driverName}`;
    if (seen.has(key)) return;
    seen.add(key);
    const resp = respByKey.get(key) ?? null;
    const claim = claimByIncident.get(incidentId) ?? null;
    const adj = resp?.adjustment_id ? args.adjustments.get(resp.adjustment_id) ?? null : null;
    entries.push({
      key,
      incidentId,
      incidentDate: inc.incident_date,
      driverName,
      role,
      vehicleNumber: inc.vehicle_number,
      category: inc.incident_category,
      faultClassification: inc.fault_classification,
      claimType: claim?.claim_type ?? null,
      claimStatus: claim?.status ?? null,
      excessApplies: inc.excess_applies,
      allocated: resp ? num(resp.allocated_amount) : null,
      paid: resp?.adjustment_id ? args.paidByAdjustment.get(resp.adjustment_id) ?? 0 : 0,
      remaining: adj && adj.remaining_balance !== null ? num(adj.remaining_balance) : null,
      deductionStatus: adj?.deduction_status ?? null,
      restartedCycle: !!resp?.restarted_cycle,
    });
  };

  // Every incident the driver was on, whatever its fault classification or claim type...
  args.incidents.forEach((i) => i.driver_name && make(i.id, i.driver_name, "driver"));
  // ...plus any they're named as responsible for on someone else's incident.
  args.responsibilities.filter((r) => !r.voided_at).forEach((r) => make(r.incident_id, r.driver_name, "responsible"));

  return entries.sort((a, b) => b.incidentDate.localeCompare(a.incidentDate) || a.driverName.localeCompare(b.driverName));
}

export async function loadDriverHistory(): Promise<HistoryEntry[]> {
  const [incRes, claimRes, respRes] = await Promise.all([
    supabase
      .from("insurance_incidents")
      .select("id, incident_date, driver_name, vehicle_number, incident_category, fault_classification, excess_applies")
      .order("incident_date", { ascending: false }),
    supabase.from("insurance_claims").select("incident_id, claim_type, status"),
    supabase
      .from("insurance_incident_responsibilities")
      .select("id, incident_id, driver_name, allocation_type, allocation_value, allocated_amount, weekly_amount, adjustment_id, restarted_cycle, fault_classification, voided_at, created_at"),
  ]);
  if (incRes.error) throw incRes.error;
  if (claimRes.error) throw claimRes.error;
  if (respRes.error) throw respRes.error;

  const responsibilities = (respRes.data as ResponsibilityRow[] | null) ?? [];
  const adjustmentIds = responsibilities.map((r) => r.adjustment_id).filter((id): id is string => !!id);
  const adjustments = new Map<string, { deduction_status: string | null; remaining_balance: number | string | null }>();
  const paidByAdjustment = new Map<string, number>();
  if (adjustmentIds.length > 0) {
    const [{ data: adjRows }, { data: paidRows }] = await Promise.all([
      supabase.from("driver_financial_adjustments").select("id, deduction_status, remaining_balance").in("id", adjustmentIds),
      supabase
        .from("driver_deduction_transactions")
        .select("adjustment_id, amount")
        .eq("txn_type", "deduction")
        .is("reversed_at", null)
        .in("adjustment_id", adjustmentIds),
    ]);
    ((adjRows as { id: string; deduction_status: string | null; remaining_balance: number | string | null }[] | null) ?? []).forEach((a) =>
      adjustments.set(a.id, a)
    );
    ((paidRows as { adjustment_id: string; amount: number | string }[] | null) ?? []).forEach((p) =>
      paidByAdjustment.set(p.adjustment_id, round2((paidByAdjustment.get(p.adjustment_id) ?? 0) + num(p.amount)))
    );
  }

  return buildDriverHistory({
    incidents: (incRes.data as Parameters<typeof buildDriverHistory>[0]["incidents"]) ?? [],
    claims: (claimRes.data as Parameters<typeof buildDriverHistory>[0]["claims"]) ?? [],
    responsibilities,
    adjustments,
    paidByAdjustment,
  });
}
